import { useEffect, useState } from "react";
import { Button, ButtonGroup, ButtonGroupItem } from "@/components/wensity/button";
import { Alert, AlertDescription } from "@/components/wensity/alert";
import { Spinner } from "@/components/wensity/spinner";
import { ContentEditor } from "@/components/admin/ContentEditor";
import { LinkSectionsEditor } from "@/components/admin/LinkSectionsEditor";
import { BackgroundEditor } from "@/components/admin/BackgroundEditor";
import { FooterLinkEditor } from "@/components/admin/FooterLinkEditor";
import { useSiteContent, useSiteContentRefresh } from "@/data/SiteContentContext";
import { DEFAULT_BACKGROUND, type SiteContentData } from "@/data/site";
import {
  getSession,
  getWebAuthnStatus,
  loginWithPasskey,
  logout as apiLogout,
  registerPasskey,
  saveContent,
} from "@/lib/adminApi";

type AdminView = "checking" | "needs-setup-token" | "can-register" | "can-login" | "authenticated";
type AdminTab = "content" | "gear" | "apps" | "services" | "footer" | "background";

export function AdminPage() {
  const data = useSiteContent();
  const refresh = useSiteContentRefresh();

  const [view, setView] = useState<AdminView>("checking");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>("content");
  const [draft, setDraft] = useState<SiteContentData>(data);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const setupToken = new URLSearchParams(window.location.search).get("setup");

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const session = await getSession();
        if (cancelled) return;
        if (session.authenticated) {
          setView("authenticated");
          return;
        }
        const status = await getWebAuthnStatus();
        if (cancelled) return;
        if (!status.registered) {
          setView(setupToken ? "can-register" : "needs-setup-token");
        } else {
          setView("can-login");
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRegister() {
    if (!setupToken) return;
    setBusy(true);
    setError(null);
    try {
      await registerPasskey(setupToken);
      await refresh();
      setView("authenticated");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogin() {
    setBusy(true);
    setError(null);
    try {
      await loginWithPasskey();
      await refresh();
      setView("authenticated");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    await apiLogout();
    setView("can-login");
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const result = await saveContent(draft);
      setDraft(result);
      await refresh();
      setSaved(true);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (view === "checking") {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (view !== "authenticated") {
    return (
      <div className="mx-auto max-w-md space-y-6 py-16 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--color-primary)]">// admin</p>
        <h1 className="text-3xl font-semibold">Admin</h1>

        {error ? (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {view === "needs-setup-token" ? (
          <p className="text-[var(--color-muted)]">
            No admin passkey has been registered yet. Open this page with the one-time setup link to
            register one.
          </p>
        ) : null}

        {view === "can-register" ? (
          <>
            <p className="text-[var(--color-muted)]">
              No admin passkey exists yet. Register one now using this device (Face ID, Touch ID,
              Windows Hello, or a security key).
            </p>
            <Button variant="brand" onClick={handleRegister} loading={busy}>
              Register passkey
            </Button>
          </>
        ) : null}

        {view === "can-login" ? (
          <>
            <p className="text-[var(--color-muted)]">Sign in with your passkey to edit content.</p>
            <Button variant="brand" onClick={handleLogin} loading={busy}>
              Sign in with passkey
            </Button>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--color-primary)]">// admin</p>
          <h1 className="text-3xl font-semibold">Content</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="brand" onClick={handleSave} loading={saving}>
            Save changes
          </Button>
          <Button variant="outline" onClick={handleLogout}>
            Sign out
          </Button>
        </div>
      </div>

      {saveError ? (
        <Alert variant="error">
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      ) : null}
      {saved ? (
        <Alert variant="success">
          <AlertDescription>Saved. Changes are live for every visitor now.</AlertDescription>
        </Alert>
      ) : null}

      <ButtonGroup>
        <ButtonGroupItem aria-pressed={activeTab === "content"} onClick={() => setActiveTab("content")}>
          Journal / Guides / Scripts
        </ButtonGroupItem>
        <ButtonGroupItem aria-pressed={activeTab === "gear"} onClick={() => setActiveTab("gear")}>
          Gear
        </ButtonGroupItem>
        <ButtonGroupItem aria-pressed={activeTab === "apps"} onClick={() => setActiveTab("apps")}>
          Apps
        </ButtonGroupItem>
        <ButtonGroupItem aria-pressed={activeTab === "services"} onClick={() => setActiveTab("services")}>
          Services
        </ButtonGroupItem>
        <ButtonGroupItem aria-pressed={activeTab === "footer"} onClick={() => setActiveTab("footer")}>
          Footer
        </ButtonGroupItem>
        <ButtonGroupItem aria-pressed={activeTab === "background"} onClick={() => setActiveTab("background")}>
          Background
        </ButtonGroupItem>
      </ButtonGroup>

      {activeTab === "content" ? (
        <ContentEditor items={draft.content} onChange={(content) => setDraft((d) => ({ ...d, content }))} />
      ) : null}
      {activeTab === "gear" ? (
        <LinkSectionsEditor
          sections={draft.gearSections}
          onChange={(gearSections) => setDraft((d) => ({ ...d, gearSections }))}
        />
      ) : null}
      {activeTab === "apps" ? (
        <LinkSectionsEditor
          sections={draft.appSections}
          onChange={(appSections) => setDraft((d) => ({ ...d, appSections }))}
        />
      ) : null}
      {activeTab === "services" ? (
        <LinkSectionsEditor
          sections={draft.serviceSections}
          onChange={(serviceSections) => setDraft((d) => ({ ...d, serviceSections }))}
        />
      ) : null}
      {activeTab === "footer" ? (
        <FooterLinkEditor
          site={draft.site}
          onChange={(patch) => setDraft((d) => ({ ...d, site: { ...d.site, ...patch } }))}
        />
      ) : null}
      {activeTab === "background" ? (
        <BackgroundEditor
          background={draft.site.background ?? DEFAULT_BACKGROUND}
          onChange={(background) => setDraft((d) => ({ ...d, site: { ...d.site, background } }))}
        />
      ) : null}
    </div>
  );
}
